import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import { ChevronRight, ChevronLeft, LayoutGrid } from 'lucide-react';
import { ListingCard } from "./ListingCard";
import type { Listing } from "../api/listings-client";
import { Link } from 'react-router-dom';

interface ViewAllCardProps {
    cardRef: React.RefObject<HTMLDivElement>;
}

const ViewAllCard: React.FC<ViewAllCardProps> = ({ cardRef }) => {
    const [cardHeight, setCardHeight] = React.useState(0);

    const updateHeight = React.useCallback(() => {
        if (cardRef.current) {
            setCardHeight(cardRef.current.clientHeight);
        }
    }, [cardRef]);

    React.useEffect(() => {
        updateHeight();
        window.addEventListener('resize', updateHeight);

        return () => {
            window.removeEventListener('resize', updateHeight);
        };
    }, [updateHeight]);

    return (
        <Link
            to="/search-results?localities="
            className="group relative w-full flex flex-col rounded-xl bg-white border border-gray-200 shadow-md transition-all duration-200 ease-in-out hover:shadow-lg select-none"
            style={{ height: cardHeight ? `${cardHeight}px` : 'auto' }}
        >
            <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-[#24ccf2]/0 via-[#2476f2]/0 to-purple-500/0 opacity-0 group-hover:opacity-100 group-hover:from-[#24ccf2]/10 group-hover:via-[#2476f2]/10 group-hover:to-purple-500/10 transition-opacity duration-200 ease-in-out -m-[1px]" />
            <div className="flex flex-col items-center justify-center w-full h-full p-6">
                <div className="mb-3 p-4 rounded-full bg-gray-50 group-hover:bg-gray-100 transition-colors duration-200">
                    <LayoutGrid className="w-10 h-10 text-gray-600" />
                </div>
                <span className="text-gray-900 font-medium">View all listings</span>
            </div>
        </Link>
    );
};

// Interface for our display items, similar to ListingResultsView
interface DisplayItem {
    stableId: string; // This ID never changes for this position
    listing: Listing | null; // Listing null implies loading
}

interface ListingsSwiperViewProps {
    listings: Listing[];
    isLoading?: boolean;
    maxCardWidth?: number;
    peekPercentage?: number;
    viewAllLink?: string;
    showViewAll?: boolean;
}

export const ListingsSwiperView: React.FC<ListingsSwiperViewProps> = ({
    listings = [],
    isLoading = false,
    maxCardWidth = 320,
    peekPercentage = 10,
    viewAllLink = "/listings",
    showViewAll = true,
}) => {
    const swiperRef = useRef<SwiperType>();
    const [isStart, setIsStart] = useState(true);
    const [isEnd, setIsEnd] = useState(false);
    const firstCardRef = useRef<HTMLDivElement>(null);
    
    // Create state to hold our display items (with stable IDs)
    const [displayItems, setDisplayItems] = useState<Array<DisplayItem>>(() => 
        Array(8).fill(null).map((_, index) => ({
            stableId: `swiper-position-${index}`, // Stable ID based on position that never changes
            listing: null
        }))
    );

    // Update display items when listings or loading state changes
    useEffect(() => {
        if (isLoading) {
            // Set all cards to loading state
            setDisplayItems(prev => 
                Array(8).fill(null).map((_, index) => ({
                    stableId: `swiper-position-${index}`,
                    listing: null
                }))
            );
        } else if (listings.length > 0) {
            // Filter valid listings
            const validListings = listings.filter(
                listing => listing.id && listing.rent && listing.suburb && listing.postcode && listing.availableFrom
            );

            // Create new display items with valid listings
            const newDisplayItems = validListings.map((listing, index) => ({
                stableId: `swiper-position-${index}`,
                listing
            }));

            setDisplayItems(newDisplayItems);
        } else {
            // No results
            setDisplayItems([]);
        }
    }, [listings, isLoading]);

    const getCardWidth = () => {
        const peekRatio = peekPercentage / 100;
        return {
            width: `min(${maxCardWidth}px, calc((100vw - 2rem) / (1 + ${peekRatio})))`,
            minWidth: '240px'
        };
    };

    const updateNavState = useCallback(() => {
        if (swiperRef.current) {
            const swiper = swiperRef.current;
            const totalSlides = displayItems.length + (showViewAll ? 1 : 0);
            const slideWidth = getCardWidth().minWidth as number;
            const containerWidth = swiper.width;
            const visibleSlides = Math.floor(containerWidth / (slideWidth + 12)); // 12 is spaceBetween

            setIsStart(swiper.isBeginning);
            
            // If all slides fit in the container, hide both arrows
            if (totalSlides <= visibleSlides) {
                setIsEnd(true);
                setIsStart(true);
            } else {
                setIsEnd(swiper.isEnd);
            }
        }
    }, [displayItems.length, showViewAll]);

    // Initial setup
    useEffect(() => {
        // Wait for Swiper to be fully initialized
        const timer = setTimeout(() => {
            updateNavState();
        }, 100);

        return () => clearTimeout(timer);
    }, [updateNavState]);

    // Handle resize
    useEffect(() => {
        const handleResize = () => {
            updateNavState();
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [updateNavState]);

    // Update nav state when listings change
    useEffect(() => {
        updateNavState();
    }, [displayItems, updateNavState]);

    const handlePrev = (e: React.MouseEvent) => {
        e.preventDefault();
        if (swiperRef.current && !isStart) {
            swiperRef.current.slidePrev();
            setTimeout(updateNavState, 100);
        }
    };

    const handleNext = (e: React.MouseEvent) => {
        e.preventDefault();
        if (swiperRef.current && !isEnd) {
            swiperRef.current.slideNext();
            setTimeout(updateNavState, 100);
        }
    };

    return (
        <div className="max-w-7xl mx-auto">
            <div className="relative overflow-visible">
                {/* Navigation Buttons */}
                {!isLoading && !isStart && (
                    <button
                        onClick={handlePrev}
                        className="absolute top-1/2 -translate-y-1/2 z-10 -left-6
                        hidden md:flex items-center justify-center
                        w-12 h-12 rounded-full
                        bg-white/90 backdrop-blur-sm
                        border border-gray-200
                        shadow-lg hover:shadow-xl
                        transition-all duration-200
                        group"
                        aria-label="Previous"
                    >
                        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#24ccf2]/0 via-[#2476f2]/0 to-purple-500/0 opacity-0 group-hover:opacity-100 group-hover:from-[#24ccf2]/10 group-hover:via-[#2476f2]/10 group-hover:to-purple-500/10 transition-opacity duration-200" />
                        <ChevronLeft className="w-6 h-6 text-gray-700 relative" />
                    </button>
                )}
                {!isLoading && !isEnd && (
                    <button
                        onClick={handleNext}
                        className="absolute top-1/2 -translate-y-1/2 z-10 -right-6
                        hidden md:flex items-center justify-center
                        w-12 h-12 rounded-full
                        bg-white/90 backdrop-blur-sm
                        border border-gray-200
                        shadow-lg hover:shadow-xl
                        transition-all duration-200
                        group"
                        aria-label="Next"
                    >
                        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#24ccf2]/0 via-[#2476f2]/0 to-purple-500/0 opacity-0 group-hover:opacity-100 group-hover:from-[#24ccf2]/10 group-hover:via-[#2476f2]/10 group-hover:to-purple-500/10 transition-opacity duration-200" />
                        <ChevronRight className="w-6 h-6 text-gray-700 relative" />
                    </button>
                )}

                {/* Slider */}
                <div className="relative md:mx-0">
                    <Swiper
                        slidesPerView="auto"
                        spaceBetween={12}
                        className="px-2 md:px-0 pb-4"
                        onBeforeInit={(swiper) => {
                            swiperRef.current = swiper;
                        }}
                        onSlideChange={updateNavState}
                        onAfterInit={updateNavState}
                    >
                        {displayItems.map((item, index) => (
                            <SwiperSlide 
                                key={item.stableId} // Use the stable ID as the key
                                style={getCardWidth()}
                            >
                                <ListingCard
                                    ref={index === 0 ? firstCardRef : null}
                                    key={item.stableId}
                                    listing={item.listing}
                                />
                            </SwiperSlide>
                        ))}

                        {showViewAll && !isLoading && displayItems.length > 0 && (
                            <SwiperSlide style={getCardWidth()}>
                                <ViewAllCard cardRef={firstCardRef} />
                            </SwiperSlide>
                        )}
                    </Swiper>
                </div>
            </div>
        </div>
    );
};

export default ListingsSwiperView;